<template>
  <aside class="ai-agent-sidebar d-none">
    <div class="ai-agent-header flex-shrink-0 p-3 border-bottom border-light">
      <div class="d-flex align-items-center justify-content-between">
        <div class="ai-brand d-flex align-items-center gap-2">
          It'sRapid AI Agent
        </div>
      </div>
    </div>
    <div class="ai-agent-body flex flex-col flex-grow-1">
      <div 
        v-for="(message, index) in messages" 
        :key="index"
        class="ai-card mb-2"
        :class="{ 'd-none': message.hidden }"
      >
        <div class="p-3">
          <!-- eslint-disable-next-line -->
          <p class="mb-0" style="white-space: pre-wrap;">{{ message.content }}</p>
        </div>
      </div>
      <div
        v-if="loading"
        class="ai-card mb-2"
      >
        <div class="p-3">
          <!-- eslint-disable-next-line -->
          <p class="mb-0 d-flex align-items-center" style="white-space: pre-wrap;">Thinking... <span class="spinner-border spinner-border-sm ml-2" role="status"><span class="sr-only">Loading...</span></span></p>
        </div>
      </div>
    </div>
    <div class="flex-shrink-0 ai-card p-2">
      <div class="input-group">
        <span class="input-group-text bg-transparent border-0">
          <i class="bi bi-chat-dots" />
        </span>
        <input 
          v-model="aiInput"
          type="text" 
          class="form-control border-0" 
          placeholder="Ask AI Agent..."
          :disabled="loading"
          @keyup.enter="sendMessage"
        >
        <button 
          type="button" 
          class="btn btn-primary"
          :disabled="loading"
          @click="sendMessage"
        >
          <div
            v-if="loading"
            class="spinner-border spinner-border-sm"
            role="status"
          >
            <span class="sr-only">Loading...</span>
          </div>
          <i
            v-else
            class="bi bi-arrow-up"
          />
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import html2canvas from 'html2canvas';

const aiAgentFunctionsWithoutWorkflow = [
    {
        name: "run_workflow",
        description: "Run a workflow.",
        parameters: {
            type: "object",
            properties: {
                workflow: {
                    type: "string",
                    enum: [
                        "create-mobile-hero-from-gtin",
                        "create-mobile-hero-from-images",
                        "create-mobile-hero-from-files"
                    ],
                    description: "The workflow to run.",
                },
            },
            required: ["workflow"],
        },
    },
];
const aiAgentFunctionsWithWorkflow = [
    {
        name: "redirect_to_internal_url",
        description: "Redirect to an internal URL.",
        parameters: {
            type: "object",
            properties: {
                url: {
                    type: "string",
                    description: "The URL to redirect to. Don't include the URL's origin.",
                },
            },
            required: ["url"],
        },
    },
    {
        name: "wait_a_second",
        description: "Wait for a second (used for waiting when loading).",
    },
    {
        name: "click_button",
        description: "Click a button.",
        parameters: {
            type: "object",
            properties: {
                elementId: {
                    type: "string",
                    description: "The id of the button to click.",
                },
            },
            required: ["elementId"],
        },
    },
    {
        name: "click_radio_button",
        description: "Click/select a radio button.",
        parameters: {
            type: "object",
            properties: {
                elementId: {
                    type: "string",
                    description: "The id of the radio button to select.",
                },
            },
            required: ["elementId"],
        },
    },
    {
        name: "click_checkbox",
        description: "Click/select a checkbox.",
        parameters: {
            type: "object",
            properties: {
                elementId: {
                    type: "string",
                    description: "The id of the checkbox to select.",
                },
            },
            required: ["elementId"],
        },
    },
    {
        name: "fill_input",
        description: "Fill an input or textarea.",
        parameters: {
            type: "object",
            properties: {
                elementId: {
                    type: "string",
                    description: "The id of the input or textarea to fill.",
                },
                text: {
                    type: "string",
                    description: "The text to fill the input or textarea with.",
                },
            },
            required: ["elementId", "text"],
        },
    },
    {
        name: "select_option_from_multi_select",
        description: "Select an option from a multi-select. You should what options are available before selecting any.",
        parameters: {
            type: "object",
            properties: {
                elementId: {
                    type: "string",
                    description: "The id of the multi-select to check options from.",
                },
                value: {
                    type: "string",
                    description: "The value of the option to select.",
                },
            },
            required: ["elementId", "value"],
        },
    },
];

const aiWorkflowPlans = {
    "create-mobile-hero-from-gtin": `Step 1: I'll redirect the user to '/dashboard/pdp-images'.
Step 2: I'll click "Choose products from library".
Step 3: I'll click the "Next" button.
Step 4: I'll add a layout name in the "Layout name" input.
Step 5: I'll enter the provided GTINs in the "Search images" input.
Step 6: I'll wait until finished loading images from database.
Step 7: I'll click one of the first GTIN image checkboxes.
Step 8: I'll click the "Next" button again.
Step 9: I'll choose from the multi-select the retailer the user provided. If the user didn't provide a retailer, I'll ask them to provide one or ask them if they want me to select one randomly from the multi-select.
Step 10: I'll choose from the multi-select the template the user provided. If the user didn't provide a template, I'll ask them to provide one or ask them if they want me to select one randomly from the multi-select.
Step 11: I'll click the "Finish" button.
Step 12: I'll see a loading indicator. When it finishes, I'll tell the user to that the mobile hero was created or inform the user about any error that occurred.`,
    "create-mobile-hero-from-images": `Tell the user that you currently can't run this workflow because it's still in development.`,
    /*
    "create-mobile-hero-from-images": `Step 1: I will first check if the user has provided the following parameters:
- One or more attached images.
- A Retailer (I can't suggest retailers as I don't have access to the database, the user must provide one).
- A template (I can't suggest templates as I don't have access to the database, the user must provide one).
- A name for the layout (I can suggest layout names).
If they haven't provided all those, I can't continue, so I'll ask them to provide those parameters.`,
    */
    "create-mobile-hero-from-files": `Tell the user that you currently can't run this workflow because it's still in development.`
};

export default {
    name: "AiAgentSidebar",
    data() {
        return {
            selectedWorkflow: '',
            currentPlan: '',
            messages: [],
            aiInput: '',
            aiFileUrls: [],
            loading: true
        };
    },
    mounted() {
        window.openAiAgentSidebar = this.open;
        window.closeAiAgentSidebar = this.close;
        window.startNewAiAgentTask = this.startNewAiAgentTask;
        window.askAiAgentForFiles = this.askAiAgentForFiles;
        window.getCurrentPageContext = this.getCurrentPageContext;
        setTimeout(() => {
            if (localStorage.getItem('aiAgentSidebar')) {
                const links = document.querySelectorAll('a');

                links.forEach(link => {
                    link.addEventListener('click', (event) => {
                        event.preventDefault();

                        const url = link.href;
                        // const answer = confirm(`This will stop the AI agent. Are you sure?`);

                        // if (answer) {
                            localStorage.removeItem('aiAgentSidebar');
                            window.location.href = url;
                        // }
                    });
                });

                /*
                window.addEventListener('beforeunload', (event) => {
                    const confirmationMessage = `This will stop the AI agent. Are you sure?`;
                    event.preventDefault();
                    event.returnValue = confirmationMessage;
                    return confirmationMessage;
                });
                */

                const aiAgentSidebar = JSON.parse(localStorage.getItem('aiAgentSidebar'));
                this.selectedWorkflow = aiAgentSidebar.selectedWorkflow;
                this.currentPlan = aiAgentSidebar.currentPlan;
                this.messages = aiAgentSidebar.messages;
                this.open();
                this.sendMessage(true);
            }
        }, 1000);
    },
    methods: {
        open() {
            document.querySelector('.c-wrapper').style.marginRight = '380px';
            document.querySelector('.ai-agent-sidebar').classList.remove('d-none');
        },
        close() {
            document.querySelector('.c-wrapper').style.marginRight = '0';
            document.querySelector('.ai-agent-sidebar').classList.add('d-none');
        },
        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        startNewAiAgentTask(prompt, fileUrls = []) {
            this.selectedWorkflow = '';
            this.currentPlan = '';
            this.messages = [];
            this.aiInput = prompt;
            this.aiFileUrls = fileUrls;
            this.sendMessage();
            this.open();
        },
        askAiAgentForFiles() {
            // ...
        },
        async callOpenAI(settings) {
            const { data } = await axios.post(`/call-open-ai`, settings);
            return data;
        },
        async addMessage(message) {
            this.messages.push(message);
            setTimeout(() => {
                const body = document.querySelector('.ai-agent-body');
                if (body) {
                    body.scrollTop = body.scrollHeight;
                }
            }, 50);
        },
        async sendMessage(fromAiAgent = false) {
            if (fromAiAgent) {
                this.addMessage({
                    role: "user",
                    content: "Continue",
                    hidden: true
                });
            } else {
                if (this.aiInput.trim() === '') {
                    this.aiInput = 'Continue';
                }
                this.addMessage({
                    role: "user",
                    content: this.aiInput.trim()
                });
            }

            this.aiInput = '';
            this.loading = true;

            try {
                const currentPageContent = this.getCurrentPageContext();

                let imageDataUrl = null;
                try {
                    imageDataUrl = await this.captureFullPageBase64();
                } catch (e) {
                    console.warn('Screenshot capture failed, proceeding without image:', e);
                }

                const baseMessages = [
                    {
                        role: "system",
                        content: `You are an autonomous AI agent. Your goal is to run app workflows interacting with UI elements. Avoid asking the user for confirmation about proceeding to act, and avoid asking the user to interact with the app, unless it's not possible for you to do that action.`
                    },
                    ...this.messages.slice(-50).map((message) => ({
                        role: message.role,
                        content: message.content
                    })),
                ];

                const userContent = [
                    { type: "text", text: `Current page context: ${JSON.stringify(currentPageContent)}` }
                ];

                if (imageDataUrl) {
                    userContent.push({ type: "image_url", image_url: { url: imageDataUrl } });
                }

                const messages = [
                    ...baseMessages,
                    { role: "user", content: userContent }
                ];

                const responseMessage = await this.callOpenAI({
                    messages,
                    model: "gpt-5",
                    functions: this.selectedWorkflow ? aiAgentFunctionsWithWorkflow : aiAgentFunctionsWithoutWorkflow
                });

                if (responseMessage.function_call) {
                    const functionName = responseMessage.function_call.name;
                    const functionArgs = JSON.parse(
                        responseMessage.function_call.arguments,
                    );

                    if (functionName === "run_workflow") {
                        this.selectedWorkflow = functionArgs.workflow;
                        this.currentPlan = aiWorkflowPlans[functionArgs.workflow];
                        this.addMessage({
                            role: "assistant",
                            content: `I'm currently running the ${this.selectedWorkflow} workflow...\n\nOk, here is the plan:\n${this.currentPlan}`
                        });
                    } else if (functionName === "redirect_to_internal_url") {
                        this.addMessage({
                            role: "assistant",
                            content: `I've redirected the user to ${functionArgs.url}`
                        });
                        this.saveAssistantStatusToLocalStorage();
                        await this.sleep(1000);
                        window.location.href = functionArgs.url;
                        return;
                    } else if (functionName === "wait_a_second") {
                        this.addMessage({
                            role: "assistant",
                            content: `I'm waiting for a moment...`
                        });
                        await this.sleep(5000);
                    } else if (functionName === "click_button") {
                        const button = document.querySelector(`[data-ai-agent-id="${functionArgs.elementId}"]`);
                        if (button) {
                            const buttonLabel = button.getAttribute('data-ai-agent-label') || 'No label';
                            button.click();
                            this.addMessage({
                                role: "assistant",
                                content: `I've clicked the button "${buttonLabel}"`
                            });
                        } else {
                            this.addMessage({
                                role: "assistant",
                                content: `I've not found the button with id ${functionArgs.elementId}`
                            });
                        }
                    } else if (functionName === "click_radio_button") {
                        const button = document.querySelector(`[data-ai-agent-id="${functionArgs.elementId}"]`);
                        if (button) {
                            const buttonLabel = button.getAttribute('data-ai-agent-label') || 'No label';
                            button.click();
                            this.addMessage({
                                role: "assistant",
                                content: `I've clicked the radio button "${buttonLabel}"`
                            });
                        } else {
                            this.addMessage({
                                role: "assistant",
                                content: `I've not found the radio button with id ${functionArgs.elementId}`
                            });
                        }
                    } else if (functionName === "click_checkbox") {
                        const button = document.querySelector(`[data-ai-agent-id="${functionArgs.elementId}"]`);
                        if (button) {
                            const buttonLabel = button.getAttribute('data-ai-agent-label') || 'No label';
                            button.click();
                            this.addMessage({
                                role: "assistant",
                                content: `I've clicked the checkbox "${buttonLabel}"`
                            });
                        } else {
                            this.addMessage({
                                role: "assistant",
                                content: `I've not found the checkbox with id ${functionArgs.elementId}`
                            });
                        }
                    } else if (functionName === "fill_input") {
                        const input = document.querySelector(`[data-ai-agent-id="${functionArgs.elementId}"]`);
                        if (input) {
                            const inputLabel = input.getAttribute('data-ai-agent-label') || 'No label';
                            input.value = functionArgs.text;
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                            input.dispatchEvent(new Event('change', { bubbles: true }));
                            this.addMessage({
                                role: "assistant",
                                content: `I've filled the input "${inputLabel}" with the text "${functionArgs.text}"`
                            });
                        } else {
                            this.addMessage({
                                role: "assistant",
                                content: `I've not found the input with id ${functionArgs.elementId}`
                            });
                        }
                    } else if (functionName === "select_option_from_multi_select") {
                        const root = document.querySelector(`[data-ai-agent-id="${functionArgs.elementId}"]`);
                        if (!root) {
                            this.addMessage({
                                role: "assistant",
                                content: `I couldn't find the element with ID "${functionArgs.elementId}"`
                            });
                            this.saveAssistantStatusToLocalStorage();
                            await this.sleep(1000);
                            this.sendMessage(true);
                            return;
                        }

                        const targetIndex = functionArgs.value;
                        const option = root.querySelector(`#null-${targetIndex} .multiselect__option`);
                        if (!option) {
                            this.addMessage({
                                role: "assistant",
                                content: `I found the element with ID "${functionArgs.elementId}" but couldn't find the option with value ${functionArgs.value}`
                            });
                            this.saveAssistantStatusToLocalStorage();
                            await this.sleep(1000);
                            this.sendMessage(true);
                            return;
                        }

                        const toggle = root.querySelector('.multiselect__select');
                        if (toggle) toggle.click();

                        option.click();

                        this.addMessage({
                            role: "assistant",
                            content: `I've selected the option with value ${functionArgs.value} from the multi-select with ID "${functionArgs.elementId}"`
                        });
                    }

                    this.saveAssistantStatusToLocalStorage();
                    await this.sleep(1000);
                    this.sendMessage(true);
                } else {
                    this.addMessage({
                        role: "assistant",
                        content: responseMessage.content
                    });
                    localStorage.removeItem('aiAgentSidebar');
                }

                this.loading = false;
            } catch (error) {
                this.aiInput = "";
                this.loading = false;
                console.error(error);
                toastr.error('Sorry, the AI agent has failed.');
            }
        },
        getCurrentPageContext() {
            const elements = document.querySelectorAll('[data-ai-agent-type]');
            return Array.from(elements).map(element => {
                if (!element.getAttribute('data-ai-agent-id')) {
                    element.setAttribute('data-ai-agent-id', 'ai-' + Math.random().toString(36).substr(2, 9));
                }
                let data = {
                    elementId: element.getAttribute('data-ai-agent-id'),
                    type: element.getAttribute('data-ai-agent-type'),
                    label: element.getAttribute('data-ai-agent-label') || '',
                    notes: element.getAttribute('data-ai-agent-notes') || ''
                };
                if (data.type.startsWith('input[type=text]')) {
                    data.value = element.value;
                }
                if (data.type.startsWith('multi-select')) {
                    const root = document.querySelector(`[data-ai-agent-id="${data.elementId}"]`);

                    const items = root.querySelectorAll(
                        '.multiselect__content li.multiselect__element[role="option"] > .multiselect__option'
                    );

                    const list = Array.from(items).map((opt, i) => {
                        const textNode = opt.querySelector('span');
                        const text = (textNode ? textNode.textContent : opt.textContent).trim();
                        const isSelected = opt.classList.contains('multiselect__option--selected');
                        const isHighlighted = opt.classList.contains('multiselect__option--highlight');
                        return {
                            value: i,
                            text,
                            isSelected,
                            isHighlighted
                        };
                    });

                    data.options = list;
                }
                return data;
            });
        },
        async captureFullPageBase64() {
            const element = document.getElementById('app');
            if (!element) {
                console.error('#app not found');
                return;
            }
 
            const canvas = await html2canvas(element, {
                backgroundColor: '#ffffff',
                useCORS: true,
                allowTaint: true
            });

            const dataUrl = canvas.toDataURL('image/png');
            return dataUrl;
        },
        saveAssistantStatusToLocalStorage() {
            localStorage.setItem('aiAgentSidebar', JSON.stringify({
                selectedWorkflow: this.selectedWorkflow,
                currentPlan: this.currentPlan,
                messages: this.messages
            }));
        }
    }
};
</script>

<style scoped lang="scss">
    .ai-agent-sidebar {
        position:fixed;
        top:0;
        right:0;
        width:380px;
        height:100vh;
        background-color:#ffffff;
        color:#212529;
        border-left:1px solid #dee2e6;
        z-index:1040;
        display:flex;
        flex-direction:column
    }
    .ai-agent-body {
        overflow:auto;
        padding:1rem;
        gap:1rem;
        display:flex;
        flex-direction:column
    }
    .ai-card {
        background:#f8f9fa;
        border:1px solid #dee2e6;
        border-radius:.75rem;
        color:#212529
    }
    .ai-card .form-control,.ai-card .form-select {
        background-color:#ffffff;
        border-color:#ced4da;
        color:#212529
    }
    .ai-card .form-control::placeholder{
        color:#6c757d;
    }
    .ai-brand{
        font-weight:700;
        letter-spacing:.2px;
        color:hsl(210, 11%, 15%);
    }
    .ai-subtle{
        color:#6c757d;
    }
    .ai-divider{
        margin:.5rem 0 1rem;
        height:1px;
        background:#dee2e6;
    }
</style>
